import React, { useEffect, useState, useRef } from "react";
import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
  Tooltip,
  InputAdornment,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { RemoveCircleOutline, Search } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import MedicineOrderPrint from "./MedicineOrderPrint";

const getCurrentDate = () => new Date().toISOString().split("T")[0];

const MedicineOrder = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const { data: stockMedicines, isLoading } = useFetchObjects(
    ["stock-medicines"],
    `stock-medicines/`,
    token
  );

  const [input, setInput] = useState({
    date: getCurrentDate(),
    customer_name: "",
    items: [],
    total_price: 0,
    discount: 0,
  });

  const [formErrors, setFormErrors] = useState({});
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const {
    handleAdd: handleAddOrder,
    resetForm,
    loading,
    responseData,
  } = useAdd(
    "medicine-orders",
    token,
    t("pharmacy.medicineOrderCreatedSuccess"),
    t("pharmacy.failedToCreateOrder")
  );

  const searchInputRef = useRef(null);
  const quantityRefs = useRef([]);
  const priceRefs = useRef([]);
  const printRef = useRef(null);
  const [printData, setPrintData] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (resetForm) {
      setInput({
        date: getCurrentDate(),
        customer_name: "",
        items: [],
        total_price: 0,
        discount: 0,
      });
      setFormErrors({});
    }
  }, [resetForm]);

  useEffect(() => {
    if (!responseData) {
      return;
    }

    setPrintData(responseData);

    const timer = setTimeout(() => {
      if (!printRef.current) {
        return;
      }

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        return;
      }

      printWindow.document.write(printRef.current.innerHTML);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 300);

    return () => clearTimeout(timer);
  }, [responseData]);

  const updateTotalBill = (items, discount = input.discount) => {
    const totalPrice = items.reduce(
      (sum, item) => sum + (parseFloat(item.total_price) || 0),
      0
    );
    const finalPrice = Math.max(totalPrice - (parseFloat(discount) || 0), 0);

    setInput((prevInput) => ({
      ...prevInput,
      total_price: parseFloat(finalPrice.toFixed(2)),
    }));
  };

  const handleDiscountChange = (e) => {
    const discount = parseFloat(e.target.value) || 0;
    setInput((prevInput) => ({
      ...prevInput,
      discount,
    }));
    updateTotalBill(input.items, discount);
  };

  const handleChangeItemField = (index, e) => {
    const { name, value } = e.target;
    const items = [...input.items];

    items[index][name] = value ? parseFloat(value) || 0 : 0;

    if (name === "quantity" || name === "sell_price") {
      const quantity = parseFloat(items[index].quantity) || 0;
      const sellPrice = parseFloat(items[index].sell_price) || 0;
      items[index].total_price = parseFloat((quantity * sellPrice).toFixed(2));
    }

    setInput({ ...input, items });
    updateTotalBill(items);
  };

  const handleAddNewItem = (medicine) => {
    if (medicine) {
      const sellPrice = parseFloat(medicine.sell_price) || 0;
      const newItem = {
        stock_medicine: medicine.id,
        name: medicine.medicine_display,
        quantity: 1,
        sell_price: sellPrice,
        total_price: parseFloat(sellPrice.toFixed(2)),
        available_stock: medicine.quantity,
      };

      const newItems = [...input.items, newItem];
      setInput((prevInput) => ({
        ...prevInput,
        items: newItems,
      }));
      updateTotalBill(newItems);
      setAutocompleteInputValue("");
      setTimeout(() => {
        quantityRefs.current[newItems.length - 1]?.focus();
      }, 50);
    }
  };

  const handleRemoveItem = (index) => {
    const items = [...input.items];
    items.splice(index, 1);
    setInput({ ...input, items });
    updateTotalBill(items);
  };

  const handleSave = (e) => {
    e.preventDefault();
    let isValid = true;
    let errors = {};

    input.items.forEach((item, index) => {
      if (!item.stock_medicine) {
        errors[`stock_medicine_${index}`] = t("pharmacy.medicineRequired");
        isValid = false;
      }
      if (!item.quantity) {
        errors[`quantity_${index}`] = t("pharmacy.quantityRequired");
        isValid = false;
      }
    });

    setFormErrors(errors);
    if (isValid) {
      const payload = {
        date: input.date,
        customer_name: input.customer_name,
        discount: input.discount,
        items: input.items.map((item) => ({
          stock_medicine: item.stock_medicine,
          quantity: item.quantity,
        })),
      };
      handleAddOrder(payload);
    }
  };

  const handleKeyDown = (index, fieldType, e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      if (fieldType === "quantity") {
        priceRefs.current[index]?.focus({ preventScroll: true });
      } else if (fieldType === "sell_price") {
        if (index < input.items.length - 1) {
          quantityRefs.current[index + 1]?.focus({ preventScroll: true });
        } else {
          searchInputRef.current?.focus();
        }
      }
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const filteredMedicines =
    autocompleteInputValue.length > 0 && stockMedicines
      ? stockMedicines.filter((medicine) =>
          medicine.medicine_display
            .toLowerCase()
            .startsWith(autocompleteInputValue.toLowerCase())
        )
      : [];

  return (
    <Paper
      sx={{
        boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.1)",
        borderRadius: "16px",
        mt: 4,
        mb: 4,
        p: 6,
        width: "100%",
        mx: "auto",
      }}
    >
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          mb: 3,
          mx: -6,
          px: 6,
          py: isScrolled ? 1.5 : 0,
          background: isScrolled
            ? "linear-gradient(135deg,#1976d2 0%,#42a5f5 100%)"
            : "transparent",
          boxShadow: isScrolled ? "0 4px 20px rgba(25,118,210,0.35)" : "none",
          borderRadius: isScrolled ? "0 0 16px 16px" : 0,
          transition: "all 0.35s cubic-bezier(.4,0,.2,1)",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Tooltip title={t("pharmacy.goBack")} arrow>
          <IconButton
            onClick={() => navigate(-1)}
            aria-label={t("pharmacy.goBack")}
            sx={{
              mr: 1,
              color: isScrolled ? "#fff" : "inherit",
              transition: "color 0.3s",
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography
          fontWeight={700}
          variant={isScrolled ? "h6" : "h4"}
          sx={{
            color: isScrolled ? "#fff" : "primary.main",
            transition: "all 0.35s",
            letterSpacing: isScrolled ? 1 : 0,
          }}
        >
          {t("pharmacy.orderMedicines")}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("pharmacy.orderDate")}
            name="date"
            type="date"
            size="medium"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={input.date}
            onChange={(e) => setInput({ ...input, date: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("pharmacy.customerName", "Customer Name")}
            name="customer_name"
            size="medium"
            variant="outlined"
            value={input.customer_name}
            onChange={(e) =>
              setInput({ ...input, customer_name: e.target.value })
            }
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Search Medicine Section */}
      <Box mb={4}>
        <Typography variant="subtitle1" fontWeight={500} mb={2}>
          {t("pharmacy.addMedicine")}
        </Typography>
        <Autocomplete
          value={null}
          inputValue={autocompleteInputValue}
          onInputChange={(event, newInputValue) => {
            setAutocompleteInputValue(newInputValue);
          }}
          onChange={(event, value) => {
            handleAddNewItem(value);
            setAutocompleteInputValue("");
          }}
          options={filteredMedicines}
          getOptionLabel={(option) => option.medicine_display}
          ListboxProps={{
            style: { maxHeight: "none", padding: 0 },
          }}
          renderOption={(props, option, { index }) => (
            <Box
              component="li"
              {...props}
              sx={{
                px: "20px",
                py: "14px",
                display: "flex",
                alignItems: "center",
                gap: 2,
                background:
                  index % 2 === 0 ? "#e3f2fd" : "#f0fdf4",
                borderLeft: "4px solid",
                borderColor: index % 2 === 0 ? "#1976d2" : "#43a047",
                transition: "background 0.2s",
                cursor: "pointer",
                "&:hover": {
                  background: "#bbdefb",
                },
              }}
            >
              <Typography
                variant="body1"
                fontWeight={700}
                sx={{ whiteSpace: "nowrap", color: "#1565c0", minWidth: 120 }}
              >
                {option.medicine_display}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography variant="body2" sx={{ whiteSpace: "nowrap", color: "#555" }}>
                <strong>{t("pharmacy.category")}:</strong> {option.category_display}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography variant="body2" sx={{ whiteSpace: "nowrap", color: "#fff", fontWeight: 700, bgcolor: "#3e2723", px: 1.2, py: 0.3, borderRadius: 1 }}>
                {option.sell_price}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography variant="body2" sx={{ whiteSpace: "nowrap", color: "#e65100" }}>
                <strong>{t("pharmacy.expiryDate")}:</strong> {option.expiry_date}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("pharmacy.searchMedicine")}
              variant="outlined"
              placeholder={t("pharmacy.searchMedicinePlaceholder")}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              inputRef={searchInputRef}
              sx={{
                backgroundColor: "#fff",
                borderRadius: 2,
              }}
            />
          )}
        />
      </Box>

      {/* Medicine List Table */}
      <TableContainer component={Paper} elevation={1} sx={{ mb: 4 }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#eef2f5" }}>
            <TableRow>
              <TableCell>{t("pharmacy.medicineName")}</TableCell>
              <TableCell align="center">
                {t("pharmacy.availableStock")}
              </TableCell>
              <TableCell align="center">{t("pharmacy.quantity")}</TableCell>
              <TableCell align="center">{t("pharmacy.price")}</TableCell>
              <TableCell align="center">{t("pharmacy.totalPrice")}</TableCell>
              <TableCell align="center">{t("pharmacy.action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {input.items.map((item, index) => (
              <TableRow
                key={index}
                sx={{

                }}
              >
                <TableCell>{item.name}</TableCell>
                <TableCell align="center">{item.available_stock}</TableCell>
                <TableCell align="center">
                  <TextField
                    inputRef={(el) => (quantityRefs.current[index] = el)}
                    name="quantity"
                    type="number"
                    size="small"
                    variant="outlined"
                    value={item.quantity}
                    onChange={(e) => handleChangeItemField(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, "quantity", e)}
                    sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    inputRef={(el) => (priceRefs.current[index] = el)}
                    name="sell_price"
                    type="number"
                    size="small"
                    variant="outlined"
                    value={item.sell_price}
                    onChange={(e) => handleChangeItemField(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, "sell_price", e)}
                    sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography fontWeight={600}>{item.total_price}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={t("pharmacy.removeItem")} arrow>
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveItem(index)}
                    >
                      <RemoveCircleOutline />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Save Button and Total Price Display */}
      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <TextField
          label={t("pharmacy.discount", "Discount")}
          name="discount"
          type="number"
          variant="outlined"
          value={input.discount}
          onChange={handleDiscountChange}
          sx={{ width: 200, backgroundColor: "#fff", borderRadius: 2 }}
        />

        <TextField
          label={t("pharmacy.totalPrice")}
          type="number"
          value={input.total_price}
          disabled
          sx={{ width: 200, backgroundColor: "#fff", borderRadius: 2 }}
        />
        <Button
          size="small"
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          sx={{
            fontWeight: 700,
            backgroundColor: "primary.main",
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          }}
        >
          {loading ? t("pharmacy.saving") : t("pharmacy.savePrescription")}
        </Button>
      </Stack>

      {printData && (
        <div ref={printRef} style={{ display: "none" }}>
          <MedicineOrderPrint data={printData} />
        </div>
      )}
    </Paper>
  );
};

export default MedicineOrder;
